import type { Core } from 'cytoscape';
import { baseClassLocalName } from '@/lib/baseClassColors';
import { classesBelowFirstLevel, type SubClassOfEdge } from './subclassDepth';

// Which elements the two temporary display filters take off the canvas
// (CLAUDE.local viz #11-12). Cytoscape hides the edges into a hidden node, so
// only nodes that would be left with nothing attached need naming here.
export function hiddenByFilters(
  cy: Core,
  opts: { hideSubclasses: boolean; hideDatatypes: boolean },
): Set<string> {
  const hide = new Set<string>();

  if (opts.hideSubclasses) {
    // Only the levels below the first: a core class keeps its immediate
    // subclasses, and the subClassOf line to each one stays with it.
    for (const id of classesBelowFirstLevel(canvasNodeIds(cy), canvasSubClassOf(cy))) {
      hide.add(id);
    }
    // A literal terminus and a terminator endpoint belong to the class they
    // hang off. When every class they attach to goes, they go too — otherwise
    // hiding subclasses leaves dashed ellipses floating with no edge.
    cy.nodes('[?isLiteral], [?isUnspecified]').forEach((n) => {
      const owners = n.connectedEdges().connectedNodes().difference(n);
      if (owners.nonempty() && owners.filter((o) => !hide.has(o.id())).empty()) {
        hide.add(n.id());
      }
    });
  }

  if (opts.hideDatatypes) {
    // Datatype-property edges + their literal terminus nodes (data.isLiteral).
    // Terminus nodes exist once Gap 4 mints them; the edge filter works
    // regardless.
    cy.edges('[classification = "DatatypeProperty"]').forEach((e) => {
      hide.add(e.id());
    });
    cy.nodes('[?isLiteral]').forEach((n) => {
      hide.add(n.id());
    });
  }

  return hide;
}

export function canvasNodeIds(cy: Core): string[] {
  return cy.nodes().map((n) => n.id());
}

// subClassOf points child -> parent, and carries the VDB's 'Hierarchy'
// classification (rdfs:subClassOf, skos:broader and the rest of the family).
//
// An edge into one of the 11 QROS base classes is dropped: the fixed frame is
// scaffolding, not a level of the user's model (the reading the structure views
// take too), so the user's own top classes are the core the depth is measured
// from. Counting the frame as a level hid a core class's own subclasses, which
// is the first half of list 27.28.
export function canvasSubClassOf(cy: Core): SubClassOfEdge[] {
  return cy
    .edges('[classification = "Hierarchy"]')
    .map((e) => ({ child: e.source().id(), parent: e.target().id() }))
    .filter((e) => baseClassLocalName(e.parent) === null);
}
